<script setup>
// The cart button and its panel. One per page however many Shop blocks
// it has: SiteShop mounts this only in the block that claimed the cart
// first. Teleported to <body> so it sits over the page rather than
// inside one block's section, which is also why its colors arrive as
// props rather than through the block's CSS variables.
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { priceText } from './shop.js';
import { cartTotal } from './shopCart.js';
import { useShopCart } from './useShopCart.js';

const props = defineProps({
    currency: { type: String, default: 'USD' },
    buttonBg: { type: String, default: '#1a1a1a' },
    buttonText: { type: String, default: '#fff' },
});

const cart = useShopCart();
const { lines, panelOpen, count, total, shipping } = cart;
const money = (n) => priceText(String(n), props.currency);
const lineText = (l) => { const t = cartTotal([l]); return t === null ? priceText(l.price, props.currency) : money(String(t)); };
const totalText = computed(() => (total.value === null ? '' : money(String(Math.round((total.value + shipping.value) * 100) / 100))));
const subtotalText = computed(() => (total.value === null ? '' : money(String(total.value))));
const shippingText = computed(() => (shipping.value > 0 ? money(String(shipping.value)) : ''));

function close() { panelOpen.value = false; }
function onKey(e) { if (e.key === 'Escape' && panelOpen.value) close(); }
// Mounted in the browser only: a teleport in the server-rendered page
// fails hydration and Vue empties the whole page. The cart lives in
// the visitor's browser anyway, so the server has nothing to draw.
const inBrowser = ref(false);
onMounted(() => { inBrowser.value = true; window.addEventListener('keydown', onKey); });
onBeforeUnmount(() => window.removeEventListener('keydown', onKey));
// Emptying the cart from inside the panel leaves nothing to look at.
watch(count, (n) => { if (n === 0) close(); });
</script>

<template>
    <Teleport v-if="inBrowser" to="body">
        <div class="tiq-cart" :style="{ '--cart-btn-bg': buttonBg, '--cart-btn-text': buttonText }">
            <button v-if="count > 0 && !panelOpen" id="tiq-cart-open" type="button" class="tiq-cart__fab"
                    :aria-label="`Open cart, ${count} ${count === 1 ? 'item' : 'items'}`" @click="panelOpen = true">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <path d="M3 4h2l2.4 11.2a2 2 0 0 0 2 1.6h7.7a2 2 0 0 0 2-1.5L21 8H6" /><circle cx="10" cy="20" r="1.3" /><circle cx="17" cy="20" r="1.3" />
                </svg>
                <span>Cart</span>
                <span class="tiq-cart__count">{{ count }}</span>
            </button>

            <div v-if="panelOpen" class="tiq-cart__scrim" @click="close" />
            <aside v-if="panelOpen" id="tiq-cart-panel" class="tiq-cart__panel" role="dialog" aria-modal="true" aria-label="Your cart">
                <header class="tiq-cart__head">
                    <h2>Your cart</h2>
                    <button type="button" class="tiq-cart__close" aria-label="Close cart" @click="close">✕</button>
                </header>
                <ul class="tiq-cart__lines">
                    <li v-for="(l, i) in lines" :key="`${l.name}|${l.option}`" class="tiq-cart__line">
                        <div class="tiq-cart__thumb">
                            <img v-if="l.image" :src="l.image" alt="" loading="lazy" />
                        </div>
                        <div class="tiq-cart__info">
                            <div class="tiq-cart__name">{{ l.name }}</div>
                            <div v-if="l.option" class="tiq-cart__opt">{{ l.option }}</div>
                            <div class="tiq-cart__qty">
                                <button type="button" :aria-label="`One fewer ${l.name}`" @click="cart.setQty(i, l.qty - 1)">−</button>
                                <span class="tiq-cart__qty-n" aria-live="polite">{{ l.qty }}</span>
                                <button type="button" :aria-label="`One more ${l.name}`" :disabled="l.qty >= 20" @click="cart.setQty(i, l.qty + 1)">+</button>
                                <button type="button" class="tiq-cart__remove" @click="cart.remove(i)">Remove</button>
                            </div>
                        </div>
                        <div class="tiq-cart__price">{{ lineText(l) }}</div>
                    </li>
                </ul>
                <footer class="tiq-cart__foot">
                    <template v-if="totalText && shippingText">
                        <div class="tiq-cart__sub"><span>Subtotal</span><span>{{ subtotalText }}</span></div>
                        <div id="tiq-cart-shipping" class="tiq-cart__sub"><span>Shipping</span><span>{{ shippingText }}</span></div>
                    </template>
                    <div v-if="totalText" class="tiq-cart__total"><span>Total</span><strong>{{ totalText }}</strong></div>
                    <a id="tiq-cart-checkout" class="tiq-cart__checkout" :href="cart.checkoutUrl()">Check out</a>
                    <p class="tiq-cart__fine">You pay by card on Stripe's secure page, and enter a shipping address there if anything is posted.</p>
                </footer>
            </aside>
        </div>
    </Teleport>
</template>

<style scoped>
.tiq-cart { font-family: inherit; color: #1a1a1a; }
.tiq-cart__fab {
    position: fixed;
    right: 16px;
    bottom: 16px;
    z-index: 9000;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 18px;
    border: 0;
    border-radius: 999px;
    background: var(--cart-btn-bg);
    color: var(--cart-btn-text);
    font: inherit;
    font-weight: 700;
    font-size: 15px;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.22);
    cursor: pointer;
}
.tiq-cart__count {
    min-width: 22px;
    padding: 1px 7px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.9);
    color: #1a1a1a;
    font-size: 13px;
    text-align: center;
}
/* Over the site's own fixed header (9999) and the template preview bar
   (10000): a cart under the header loses its heading and close button
   on a phone. */
.tiq-cart__scrim { position: fixed; inset: 0; z-index: 10001; background: rgba(0, 0, 0, 0.35); }
.tiq-cart__panel {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    z-index: 10002;
    width: min(420px, 100vw);
    display: flex;
    flex-direction: column;
    background: #fff;
    box-shadow: -8px 0 30px rgba(0, 0, 0, 0.18);
}
.tiq-cart__head { display: flex; align-items: center; justify-content: space-between; padding: 18px 20px; border-bottom: 1px solid #ececec; }
.tiq-cart__head h2 { margin: 0; font-size: 20px; font-weight: 800; }
.tiq-cart__close { border: 0; background: none; font-size: 18px; padding: 4px 8px; cursor: pointer; color: #555; }
.tiq-cart__lines { list-style: none; margin: 0; padding: 8px 20px; overflow-y: auto; flex: 1; }
.tiq-cart__line { display: grid; grid-template-columns: 64px 1fr auto; gap: 12px; padding: 14px 0; border-bottom: 1px solid #f0f0f0; }
.tiq-cart__thumb { width: 64px; height: 64px; border-radius: 8px; background: #f1efe9; overflow: hidden; }
.tiq-cart__thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
.tiq-cart__name { font-weight: 700; font-size: 15px; line-height: 1.3; }
.tiq-cart__opt { margin-top: 2px; font-size: 13px; color: #666; }
.tiq-cart__qty { margin-top: 8px; display: flex; align-items: center; gap: 6px; }
.tiq-cart__qty button:not(.tiq-cart__remove) {
    width: 30px; height: 30px; border: 1px solid #d6d6d6; border-radius: 6px; background: #fff; font: inherit; font-size: 16px; cursor: pointer;
}
.tiq-cart__qty button:disabled { opacity: 0.4; cursor: default; }
.tiq-cart__qty-n { min-width: 22px; text-align: center; font-weight: 700; }
.tiq-cart__remove { margin-left: 8px; border: 0; background: none; padding: 0; font: inherit; font-size: 13px; color: #666; text-decoration: underline; cursor: pointer; }
.tiq-cart__price { font-weight: 700; font-size: 15px; white-space: nowrap; }
.tiq-cart__foot { padding: 16px 20px 20px; border-top: 1px solid #ececec; }
.tiq-cart__sub { display: flex; justify-content: space-between; font-size: 14px; color: #555; margin-bottom: 6px; }
.tiq-cart__total { display: flex; justify-content: space-between; font-size: 17px; margin-bottom: 12px; }
.tiq-cart__checkout {
    display: block;
    padding: 13px 16px;
    border-radius: 8px;
    background: var(--cart-btn-bg);
    color: var(--cart-btn-text);
    text-align: center;
    font-weight: 700;
    font-size: 16px;
    text-decoration: none;
}
.tiq-cart__checkout:hover { filter: brightness(0.94); }
.tiq-cart__fine { margin: 10px 0 0; font-size: 12px; line-height: 1.4; color: #6b7280; text-align: center; }
</style>
